import React, { useState } from 'react';
import '../styles/ViewerConnectDialog.css';
import { ConnectionStatus } from '../utils/webRtcManager';

interface ViewerConnectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  status: ConnectionStatus;
  currentSessionId: string;
  logs?: string[];
  onConnect: (pin: string) => void;
  onDisconnect: () => void;
}

export const ViewerConnectDialog: React.FC<ViewerConnectDialogProps> = ({
  isOpen,
  onClose,
  status,
  currentSessionId,
  logs = [],
  onConnect,
  onDisconnect
}) => {
  const [pinInput, setPinInput] = useState(currentSessionId || '');
  const [errorMsg, setErrorMsg] = useState('');
  const [showLogs, setShowLogs] = useState(false);

  // Synchroniseer pinInput wanneer dialoog opent of currentSessionId wijzigt
  React.useEffect(() => {
    if (isOpen) {
      setPinInput(currentSessionId || '');
      setErrorMsg('');
    }
  }, [isOpen, currentSessionId]);

  if (!isOpen) return null;

  const handleConnect = () => {
    const cleaned = pinInput.trim();
    if (!cleaned || cleaned.length < 4) {
      setErrorMsg('Voer de 4-cijferige koppelcode van de iPad in');
      return;
    }
    setErrorMsg('');
    onConnect(cleaned);
  };

  const isConnected = status === 'connected';
  const isConnecting = status === 'connecting';

  return (
    <div className="viewer-dialog-backdrop" onClick={onClose}>
      <div className="viewer-dialog-card" onClick={e => e.stopPropagation()}>
        <div className="viewer-dialog-header">
          <h3>📺 Toeschouwersscherm Koppelen</h3>
          <button className="viewer-dialog-close" onClick={onClose}>✕</button>
        </div>

        <div className="viewer-dialog-body">
          <p className="viewer-dialog-intro">
            Verbind het tabletscherm (iPad) direct over jouw hotspot of Wi-Fi netwerk.
          </p>

          <div className="viewer-status-row">
            <span className="label">Status:</span>
            <span className={`status-pill ${status}`}>
              {isConnected && '● Verbonden'}
              {isConnecting && 'Verbinden...'}
              {status === 'disconnected' && 'Niet verbonden'}
              {status === 'error' && 'Fout / opnieuw proberen'}
            </span>
          </div>

          {isConnected ? (
            <div className="viewer-connected-box">
              <div className="connected-icon">✓</div>
              <p>De iPad ontvangt live alle punten, games en serveerwissels realtime.</p>
              <button className="btn-disconnect" onClick={onDisconnect}>
                Verbinding verbreken
              </button>
            </div>
          ) : (
            <div className="viewer-input-group">
              <label>Koppelcode van het tabletscherm (iPad):</label>
              
              {/* Invoervak voor 4-cijferige code */}
              <div className="pin-input-container">
                <input
                  type="text"
                  maxLength={6}
                  placeholder="Bijv. 1664"
                  value={pinInput}
                  onChange={(e) => {
                    setPinInput(e.target.value.replace(/\D/g, ''));
                    setErrorMsg('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleConnect();
                  }}
                  autoFocus
                />
              </div>

              {/* Knop Verbinden direct ONDER het invoervak */}
              <button 
                className="btn-connect" 
                onClick={handleConnect}
                disabled={pinInput.length < 4}
              >
                {isConnecting ? 'Verbinden...' : 'Verbinden'}
              </button>

              {errorMsg && <div className="error-text">{errorMsg}</div>}

              {isConnecting && (
                <div style={{ marginTop: '10px', textAlign: 'center' }}>
                  <button 
                    style={{ background: 'none', border: 'none', color: '#ff6b6b', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.85rem' }}
                    onClick={() => {
                      onDisconnect();
                      setPinInput('');
                    }}
                  >
                    Koppeling annuleren
                  </button>
                </div>
              )}
              
              <div className="viewer-instructions">
                <strong>Stappenplan:</strong>
                <ol>
                  <li>Zet op je telefoon de <em>Persoonlijke Hotspot</em> aan.</li>
                  <li>Verbind de iPad via Wi-Fi met deze hotspot.</li>
                  <li>Open op de iPad: <code>squash.aiqagents.nl/viewer</code></li>
                  <li>Vul de 4-cijferige code van de iPad hierboven in en tik op <strong>Verbinden</strong>.</li>
                </ol>
              </div>
            </div>
          )}

          {/* Real-time Diagnostics Log Box */}
          <div className="viewer-logs-toggle-row">
            <button 
              type="button" 
              className="btn-toggle-logs"
              onClick={() => setShowLogs(prev => !prev)}
            >
              {showLogs ? 'Verberg verbindingslog ▲' : `Bekijk verbindingslog (${logs.length}) ▼`}
            </button>
          </div>

          {showLogs && (
            <div className="viewer-log-box">
              <div className="viewer-log-title">📋 Live activiteit:</div>
              <div className="viewer-log-entries">
                {logs.length === 0 ? (
                  <div className="viewer-log-empty">Nog geen activiteit geregistreerd</div>
                ) : (
                  logs.map((l, idx) => (
                    <div key={idx} className="viewer-log-line">{l}</div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewerConnectDialog;
